import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { InvoiceType } from '../../models/allocation.model';
import { Project } from '../../models/project.model';
import { MilestonePlanTask, MilestonePlanTaskService } from '../../services/milestone-plan-task.service';
import { PaymentTrackingItem, PaymentTrackingItemService } from '../../services/payment-tracking-item.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { ProjectService } from '../../services/project.service';

interface SelectOption<TValue = string | number> {
  label: string;
  value: TValue;
}

interface MilestoneStatusRow {
  project: string;
  manager: string;
  accountManager: string;
  milestone: string;
  dueDate: string;
  status: 'Raised' | 'Paid' | 'Pending' | 'Overdue' | 'Not due';
  dueDateObj: Date | null;
}

interface InvoiceStatusBreakdownRow {
  status: 'Paid' | 'Raised' | 'Pending' | 'Overdue';
  count: number;
}

interface PaymentAlertRow {
  project: string;
  note: string;
  severity: 'high' | 'medium' | 'low';
}

@Component({
  selector: 'app-invoicing-milestone-tracker',
  standalone: true,
  imports: [CommonModule, FormsModule, Select, TableModule],
  templateUrl: './invoicing-milestone-tracker.component.html',
  styleUrl: './invoicing-milestone-tracker.component.scss'
})
export class InvoicingMilestoneTrackerComponent implements OnInit, OnDestroy {
  yearOptions: SelectOption<number>[] = [];
  managerOptions: SelectOption<string | null>[] = [{ label: 'All', value: null }];
  accountManagerOptions: SelectOption<string | null>[] = [{ label: 'All', value: null }];

  readonly monthOptions: SelectOption<number>[] = [
    { label: 'Jan', value: 1 },
    { label: 'Feb', value: 2 },
    { label: 'Mar', value: 3 },
    { label: 'Apr', value: 4 },
    { label: 'May', value: 5 },
    { label: 'Jun', value: 6 },
    { label: 'Jul', value: 7 },
    { label: 'Aug', value: 8 },
    { label: 'Sep', value: 9 },
    { label: 'Oct', value: 10 },
    { label: 'Nov', value: 11 },
    { label: 'Dec', value: 12 }
  ];

  selectedYear: number | null = new Date().getFullYear();
  selectedMonth: number | null = new Date().getMonth() + 1;
  selectedManager: string | null = null;
  selectedAccountManager: string | null = null;

  milestonesDueThisMonth = 8;
  invoicesRaised = 0;
  awaitingInvoice = 0;
  overdueInvoices = 0;
  loading = true;

  milestoneRows: MilestoneStatusRow[] = [];
  invoiceStatusRows: InvoiceStatusBreakdownRow[] = [
    { status: 'Paid', count: 0 },
    { status: 'Raised', count: 0 },
    { status: 'Pending', count: 0 },
    { status: 'Overdue', count: 0 }
  ];
  paymentAlertRows: PaymentAlertRow[] = [];

  private allInvoiceRows: MilestoneStatusRow[] = [];
  private allMilestoneTasks: MilestonePlanTask[] = [];

  constructor(
    private projectService: ProjectService,
    private paymentTrackingItemService: PaymentTrackingItemService,
    private milestonePlanTaskService: MilestonePlanTaskService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    // Enable the header funnel icon so the filter bar collapses/expands like
    // every other page.
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.loadLiveData();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.setCanShowFilterToggle(false);
  }

  onFilterChange(): void {
    this.applyPeriodFilters();
  }

  /**
   * True if any clearable filter is applied (shows the clear icon).
   * Year/Month are deliberately excluded: they default to the current period
   * rather than "empty", so they are a period selector, not a filter — the same
   * treatment Work Log Report gives its period controls.
   */
  hasActiveFilters(): boolean {
    return !!(this.selectedAccountManager || this.selectedManager);
  }

  clearFilters(): void {
    this.selectedAccountManager = null;
    this.selectedManager = null;
    this.onFilterChange();
  }

  statusClass(status: MilestoneStatusRow['status']): string {
    switch (status) {
      case 'Paid':
        return 'status-chip status-chip--paid';
      case 'Raised':
        return 'status-chip status-chip--raised';
      case 'Pending':
        return 'status-chip status-chip--pending';
      case 'Overdue':
        return 'status-chip status-chip--overdue';
      default:
        return 'status-chip status-chip--neutral';
    }
  }

  alertClass(severity: PaymentAlertRow['severity']): string {
    switch (severity) {
      case 'high':
        return 'alert-amount alert-amount--high';
      case 'medium':
        return 'alert-amount alert-amount--medium';
      default:
        return 'alert-amount alert-amount--low';
    }
  }

  private loadLiveData(): void {
    this.loading = true;
    this.projectService.getAllProjects().pipe(
      catchError(() => of([] as Project[]))
    ).subscribe(projects => {
      const activeProjects = projects.filter(
        project =>
          project.isActive &&
          (project.invoiceType === InvoiceType.Monthly || project.invoiceType === InvoiceType.ProjectMilestone)
      );

      if (activeProjects.length === 0) {
        this.allInvoiceRows = [];
        this.allMilestoneTasks = [];
        this.yearOptions = this.getDefaultYearOptions();
        this.managerOptions = [{ label: 'All', value: null }];
        this.accountManagerOptions = [{ label: 'All', value: null }];
        this.applyPeriodFilters();
        this.loading = false;
        return;
      }

      const invoiceRequests = activeProjects.map(project =>
        this.paymentTrackingItemService.getByProjectId(project.id).pipe(
          catchError(() => of([] as PaymentTrackingItem[]))
        )
      );
      const milestoneRequests = activeProjects.map(project =>
        this.milestonePlanTaskService.getByProjectId(project.id).pipe(
          catchError(() => of([] as MilestonePlanTask[]))
        )
      );

      forkJoin([forkJoin(invoiceRequests), forkJoin(milestoneRequests)]).subscribe({
        next: ([invoiceLists, milestoneLists]) => {
          this.allInvoiceRows = [];
          this.allMilestoneTasks = [];

          activeProjects.forEach((project, index) => {
            const label = this.projectLabel(project);
            const invoices = invoiceLists[index] ?? [];
            const milestones = milestoneLists[index] ?? [];

            milestones.forEach(task => this.allMilestoneTasks.push(task));
            invoices.forEach(invoice => {
              const dueDateObj = this.parseDate(invoice.targetDate);
              this.allInvoiceRows.push({
                project: label,
                manager: (project.managerName || 'Unassigned').trim(),
                accountManager: (project.accountManagerName || 'Unassigned').trim(),
                milestone: invoice.paymentMilestone || `Invoice ${invoice.number}`,
                dueDate: this.formatDate(dueDateObj),
                dueDateObj,
                status: this.mapInvoiceStatus(invoice.invoicingStatus, dueDateObj)
              });
            });
          });

          this.yearOptions = this.buildYearOptions();
          this.managerOptions = this.buildTextOptions(this.allInvoiceRows.map(row => row.manager));
          this.accountManagerOptions = this.buildTextOptions(this.allInvoiceRows.map(row => row.accountManager));
          if (!this.yearOptions.some(option => option.value === this.selectedYear)) {
            this.selectedYear = this.yearOptions[0]?.value ?? new Date().getFullYear();
          }
          this.applyPeriodFilters();
          this.loading = false;
        },
        error: () => {
          this.allInvoiceRows = [];
          this.allMilestoneTasks = [];
          this.yearOptions = this.getDefaultYearOptions();
          this.applyPeriodFilters();
          this.loading = false;
        }
      });
    });
  }

  private applyPeriodFilters(): void {
    const now = new Date();
    const filteredInvoices = this.allInvoiceRows
      .filter(row => this.isInSelectedPeriod(row.dueDateObj))
      .filter(row => !this.selectedManager || row.manager === this.selectedManager)
      .filter(row => !this.selectedAccountManager || row.accountManager === this.selectedAccountManager)
      .sort((a, b) => (a.dueDateObj?.getTime() ?? Number.MAX_SAFE_INTEGER) - (b.dueDateObj?.getTime() ?? Number.MAX_SAFE_INTEGER));

    this.milestoneRows = filteredInvoices;
    this.invoicesRaised = filteredInvoices.filter(row => row.status === 'Raised').length;
    this.overdueInvoices = filteredInvoices.filter(row => row.status === 'Overdue').length;
    this.awaitingInvoice = filteredInvoices.filter(row => row.status === 'Pending').length;

    this.milestonesDueThisMonth = this.allMilestoneTasks.filter(task => {
      const date = this.parseDate(task.targetDate);
      if (!date) {
        return false;
      }
      const yearMatches = this.selectedYear == null || date.getFullYear() === this.selectedYear;
      const monthMatches = this.selectedMonth == null || (date.getMonth() + 1) === this.selectedMonth;
      return yearMatches && monthMatches;
    }).filter(task => {
      if (!this.selectedManager && !this.selectedAccountManager) {
        return true;
      }
      const matchingInvoice = this.allInvoiceRows.find(row => {
        const due = row.dueDateObj;
        if (!due) {
          return false;
        }
        const yearMatches = this.selectedYear == null || due.getFullYear() === this.selectedYear;
        const monthMatches = this.selectedMonth == null || (due.getMonth() + 1) === this.selectedMonth;
        if (!yearMatches || !monthMatches) {
          return false;
        }
        const managerOk = !this.selectedManager || row.manager === this.selectedManager;
        const accountOk = !this.selectedAccountManager || row.accountManager === this.selectedAccountManager;
        return managerOk && accountOk;
      });
      return !!matchingInvoice;
    }).length;

    this.invoiceStatusRows = [
      { status: 'Paid', count: filteredInvoices.filter(row => row.status === 'Paid').length },
      { status: 'Raised', count: this.invoicesRaised },
      { status: 'Pending', count: this.awaitingInvoice },
      { status: 'Overdue', count: this.overdueInvoices }
    ];

    this.paymentAlertRows = filteredInvoices
      .filter(row => row.status === 'Overdue' || row.status === 'Pending')
      .slice(0, 5)
      .map(row => {
        const daysDiff = row.dueDateObj
          ? Math.ceil((row.dueDateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
          : 0;
        const overdue = daysDiff < 0;
        return {
          project: row.project,
          note: overdue
            ? `Due ${row.dueDate} — ${Math.abs(daysDiff)} day(s) overdue`
            : `Due ${row.dueDate} — ${daysDiff} day(s) remaining`,
          severity: overdue ? 'high' : 'medium'
        } as PaymentAlertRow;
      });
  }

  private isInSelectedPeriod(date: Date | null): boolean {
    if (!date) {
      return false;
    }
    const yearMatches = this.selectedYear == null || date.getFullYear() === this.selectedYear;
    const monthMatches = this.selectedMonth == null || (date.getMonth() + 1) === this.selectedMonth;
    return yearMatches && monthMatches;
  }

  private mapInvoiceStatus(rawStatus: string, targetDate: Date | null): MilestoneStatusRow['status'] {
    const status = (rawStatus || '').trim().toLowerCase();
    if (status === 'paid') {
      return 'Paid';
    }
    if (status === 'invoiced' || status === 'raised') {
      return 'Raised';
    }
    if (status === 'not invoiced') {
      return 'Not due';
    }
    if (targetDate && targetDate < new Date()) {
      return 'Overdue';
    }
    return 'Pending';
  }

  private projectLabel(project: Project): string {
    const code = (project.projectCode || '').trim() || project.id.slice(0, 4).toUpperCase();
    const name = (project.name || 'Project').trim();
    return `${code} — ${name}`;
  }

  private parseDate(value?: string): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDate(date: Date | null): string {
    if (!date) {
      return '-';
    }
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    }).format(date);
  }

  private buildYearOptions(): SelectOption<number>[] {
    const years = new Set<number>();
    this.allInvoiceRows.forEach(row => {
      if (row.dueDateObj) {
        years.add(row.dueDateObj.getFullYear());
      }
    });
    if (!years.size) {
      return this.getDefaultYearOptions();
    }
    return Array.from(years)
      .sort((a, b) => b - a)
      .map(year => ({ label: String(year), value: year }));
  }

  private getDefaultYearOptions(): SelectOption<number>[] {
    const current = new Date().getFullYear();
    return [current - 1, current, current + 1].map(year => ({ label: String(year), value: year }));
  }

  private buildTextOptions(values: string[]): SelectOption<string | null>[] {
    const unique = Array.from(new Set(values.map(v => (v || 'Unassigned').trim()))).sort((a, b) => a.localeCompare(b));
    return [{ label: 'All', value: null }, ...unique.map(value => ({ label: value, value }))];
  }
}
